// The library's upload queue (S1): files go up one after another (audit frontend C.18: no cap on
// parallel uploads). Each is checked first, then prepared, sent to the bucket and completed; its
// row keeps the step, the progress, any refusal and the name the library kept. The queue is the
// app's, as live's global store (spec upload-tray): `UploadsProvider` holds it above every
// signed-in page, so its uploads go on and its rows stay as the user leaves the library.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { getListRecentFoldersQueryKey } from "../../api/generated/endpoints/assets/assets";
import { refreshLibraryLists } from "../../api/library-lists";
import { refusalOf, uploadFile, type UploadStep } from "../../api/upload-flow";
import { attemptKey, lostAttempt, type LostAttempt } from "../idempotency";
import { toUserMessage } from "../messages";

export type UploadStatus = "waiting" | UploadStep["step"] | "done" | "failed" | "canceled";

/** Where a file goes: its team, brand and folder (null: the brand's top level). */
export interface UploadPlace {
  teamId: string;
  brandId: string;
  folderId: string | null;
}

export interface UploadEntry {
  id: string;
  file: File;
  /** Where it goes, so it goes on there whatever page the user opens meanwhile. */
  place: UploadPlace;
  status: UploadStatus;
  progress: number;
  /** Why it failed, in words. */
  problem: string | undefined;
  /** The attempt whose answer was lost, kept only to resend its key (proposal 3.2). */
  lost: LostAttempt | undefined;
  /** Set while the upload runs, so it can be canceled. */
  controller: AbortController | undefined;
  /** The name the library keeps, once uploaded: the server may suffix a taken one. */
  savedAs: string | undefined;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/** A file that may be sent again: it failed or was canceled, and passes the checks. */
export function canRetry(entry: UploadEntry): boolean {
  const stopped = entry.status === "failed" || entry.status === "canceled";
  return stopped && refusalOf(entry.file) === undefined;
}

/** A file still on its way: waiting, or in one of the steps. */
export function isActive(entry: UploadEntry): boolean {
  return !["done", "failed", "canceled"].includes(entry.status);
}

export function useUploadQueue() {
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const queryClient = useQueryClient();
  // Through a mutation, so an ended session reaches the shell's one handler (F3).
  const upload = useMutation({ mutationFn: uploadFile });
  // Files canceled while they waited: their batch skips them.
  const skipped = useRef(new Set<string>());
  // What may be canceled now, kept outside the render so "Cancel all" never reads a stale list:
  // the running uploads' controllers, and the files still waiting their turn.
  const running = useRef(new Map<string, AbortController>());
  const waiting = useRef(new Set<string>());
  const update = (id: string, patch: Partial<UploadEntry>) =>
    setEntries((current) => current.map((each) => (each.id === id ? { ...each, ...patch } : each)));

  const run = async (entry: UploadEntry) => {
    const controller = new AbortController();
    running.current.set(entry.id, controller);
    waiting.current.delete(entry.id);
    // One entry is one file, so its id stands for the body a retry sends again.
    const key = attemptKey(entry.id, entry.lost);
    update(entry.id, { status: "preparing", problem: undefined, controller, lost: undefined });
    const { teamId, brandId, folderId } = entry.place;
    try {
      const asset = await upload.mutateAsync({
        teamId,
        brandId,
        folderId,
        file: entry.file,
        key,
        signal: controller.signal,
        onStep: ({ step, progress }) => update(entry.id, { status: step, progress }),
      });
      update(entry.id, {
        status: "done",
        progress: 1,
        controller: undefined,
        savedAs: asset.name,
      });
      const recent = getListRecentFoldersQueryKey(teamId, brandId);
      await Promise.all([
        refreshLibraryLists(queryClient, teamId, brandId),
        // Its complete counts the folder as the member's use: Home's Recent Projects reads again.
        folderId === null ? null : queryClient.invalidateQueries({ queryKey: recent }),
      ]);
    } catch (error) {
      if (isAbort(error)) {
        update(entry.id, { status: "canceled", controller: undefined });
        return;
      }
      update(entry.id, {
        status: "failed",
        problem: toUserMessage(error),
        controller: undefined,
        // No answer arrived, so the server may have the attempt: a retry resends its key.
        lost: lostAttempt(key, entry.id, error),
      });
    } finally {
      running.current.delete(entry.id);
    }
  };

  /** Queues the files into a library and uploads them in order; one failing a check is not sent. */
  const add = async (files: File[], place: UploadPlace) => {
    const fresh = files.map((file): UploadEntry => {
      const refusal = refusalOf(file);
      return {
        id: crypto.randomUUID(),
        file,
        place,
        status: refusal === undefined ? "waiting" : "failed",
        progress: 0,
        problem: refusal,
        lost: undefined,
        controller: undefined,
        savedAs: undefined,
      };
    });
    setEntries((current) => [...current, ...fresh]);
    for (const entry of fresh) if (entry.status === "waiting") waiting.current.add(entry.id);
    for (const entry of fresh) {
      if (entry.status === "waiting" && !skipped.current.has(entry.id)) await run(entry);
    }
  };

  /** Cancels a running upload, or a waiting one before it starts. */
  const cancel = (id: string) => {
    const controller = running.current.get(id);
    if (controller !== undefined) {
      controller.abort();
      return;
    }
    if (waiting.current.delete(id)) {
      skipped.current.add(id);
      update(id, { status: "canceled" });
    }
  };

  return {
    entries,
    add,
    retry: (entry: UploadEntry) => void run(entry),
    cancel: (entry: UploadEntry) => cancel(entry.id),
    // From the refs, not this render's list: a file queued a moment ago is canceled too.
    cancelAll: () => [...running.current.keys(), ...waiting.current].forEach(cancel),
    remove: (entry: UploadEntry) =>
      setEntries((current) => current.filter((each) => each.id !== entry.id)),
    clear: () => setEntries((current) => current.filter(isActive)),
  };
}

export type UploadQueue = ReturnType<typeof useUploadQueue>;
